import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCcw, Brain } from "lucide-react";
import { formatDate } from "@/lib/formatDate";

const SOURCE_LABELS = { inbox: "Posteingang", rebook: "Umbuchung" };

export function SettingsAiLearningSection({ companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/ai-learning-log`);
      setItems(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-4 border border-slate-200 bg-white shadow-sm mt-4" data-testid="settings-ai-learning-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-violet-700" />
          <h3 className="text-sm font-semibold text-slate-800">KI-Lernverlauf</h3>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} title="Lernverlauf neu laden" data-testid="ai-learning-refresh-button">
          <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Jedes Mal, wenn du einen KI-Kontovorschlag korrigierst, lernt die KI eine Regel und
        speichert sie am Konto. Hier siehst du alle gelernten Regeln — löschen kannst du sie
        beim jeweiligen Konto im Kontenrahmen.
      </p>
      {items.length === 0 ? (
        <div className="text-sm text-slate-400 py-4" data-testid="ai-learning-empty">
          Noch keine Lernregeln — korrigiere einen KI-Kontovorschlag im Posteingang oder per
          Umbuchen, dann erscheint der Eintrag hier.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Datum</TableHead>
              <TableHead>Gelernte Regel</TableHead>
              <TableHead>Konto (neu)</TableHead>
              <TableHead>Vorher (KI)</TableHead>
              <TableHead>Auslösender Beleg</TableHead>
              <TableHead>Quelle</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`ai-learning-row-${e.id}`}>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                  {e.created_at ? formatDate(e.created_at) : "—"}
                </TableCell>
                <TableCell className="text-sm text-violet-800 max-w-[300px]">
                  <span className="bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5 inline-block">
                    {e.rule}
                  </span>
                </TableCell>
                <TableCell className="text-sm font-medium">{e.account_code || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-[160px] truncate" title={e.old_account || ""}>
                  {e.old_account || "—"}
                </TableCell>
                <TableCell className="text-xs text-slate-600 max-w-[200px]">
                  <div className="truncate" title={e.context?.supplier || ""}>{e.context?.supplier || "—"}</div>
                  {e.context?.document && (
                    <div className="text-[11px] text-slate-400 truncate" title={e.context.document}>{e.context.document}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {SOURCE_LABELS[e.context?.source] || e.context?.source || "—"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
